<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 900 1200"
    preserveAspectRatio="xMidYMid slice"
    class="absolute inset-0 w-full h-full"
  >
    <defs>
      <linearGradient :id="`primaryGrad-${theme}`" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop v-if="theme === 'light'" offset="0%" :style="`stop-color:var(--p-primary-50);stop-opacity:1`" />
        <stop v-if="theme === 'light'" offset="50%" :style="`stop-color:var(--p-primary-100);stop-opacity:1`" />
        <stop v-if="theme === 'light'" offset="100%" :style="`stop-color:var(--p-primary-200);stop-opacity:1`" />
        <stop v-if="theme === 'dark'" offset="0%" :style="`stop-color:var(--p-surface-900);stop-opacity:1`" />
        <stop v-if="theme === 'dark'" offset="50%" :style="`stop-color:var(--p-surface-800);stop-opacity:1`" />
        <stop v-if="theme === 'dark'" offset="100%" :style="`stop-color:var(--p-surface-700);stop-opacity:1`" />
      </linearGradient>

      <linearGradient :id="`accentGrad-${theme}`" x1="100%" y1="0%" x2="0%" y2="100%">
        <stop
          offset="0%"
          :style="`stop-color:var(--p-primary-color);stop-opacity:${theme === 'light' ? 0.3 : 0.15}`"
        />
        <stop
          offset="100%"
          :style="`stop-color:var(--p-primary-300);stop-opacity:${theme === 'light' ? 0.2 : 0.1}`"
        />
      </linearGradient>

      <!-- Grid Pattern -->
      <pattern v-if="pattern === 'grid'" :id="`smallGrid-${theme}`" width="20" height="20" patternUnits="userSpaceOnUse">
        <path d="M 20 0 L 0 0 0 20" fill="none" :stroke="strokeColor" stroke-width="0.5" opacity="0.15" />
      </pattern>

      <pattern v-if="pattern === 'grid'" :id="`mainPattern-${theme}`" :width="patternSize" :height="patternSize" patternUnits="userSpaceOnUse">
        <rect :width="patternSize" :height="patternSize" :fill="`url(#smallGrid-${theme})`" />
        <path :d="`M ${patternSize} 0 L 0 0 0 ${patternSize}`" fill="none" :stroke="strokeColor" stroke-width="1" :opacity="strokeOpacity" />
      </pattern>

      <!-- Hexagon Pattern -->
      <pattern v-if="pattern === 'hexagon'" :id="`mainPattern-${theme}`" :width="patternSize" :height="patternSize * 0.866" patternUnits="userSpaceOnUse">
        <path
          :d="`M ${patternSize * 0.25} ${patternSize * 0.433} L ${patternSize * 0.75} ${patternSize * 0.433} L ${patternSize} ${patternSize * 0.866} L ${patternSize * 0.75} ${patternSize * 1.299} L ${patternSize * 0.25} ${patternSize * 1.299} L 0 ${patternSize * 0.866} Z`"
          fill="none"
          :stroke="strokeColor"
          stroke-width="1"
          :opacity="strokeOpacity"
        />
      </pattern>

      <!-- Triangle Pattern -->
      <pattern v-if="pattern === 'triangle'" :id="`mainPattern-${theme}`" :width="patternSize" :height="patternSize" patternUnits="userSpaceOnUse">
        <path :d="`M 0 ${patternSize} L ${patternSize / 2} 0 L ${patternSize} ${patternSize} Z`" fill="none" :stroke="strokeColor" stroke-width="1" :opacity="strokeOpacity" />
        <path :d="`M ${patternSize / 2} 0 L ${patternSize} ${patternSize} L ${patternSize} 0 Z`" fill="none" :stroke="strokeColor" stroke-width="1" :opacity="strokeOpacity * 0.7" />
      </pattern>

      <!-- Diamond Pattern -->
      <pattern v-if="pattern === 'diamond'" :id="`mainPattern-${theme}`" :width="patternSize" :height="patternSize" patternUnits="userSpaceOnUse">
        <path :d="`M ${patternSize / 2} 0 L ${patternSize} ${patternSize / 2} L ${patternSize / 2} ${patternSize} L 0 ${patternSize / 2} Z`" fill="none" :stroke="strokeColor" stroke-width="1" :opacity="strokeOpacity" />
      </pattern>

      <!-- Dots Pattern -->
      <pattern v-if="pattern === 'dots'" :id="`mainPattern-${theme}`" :width="patternSize" :height="patternSize" patternUnits="userSpaceOnUse">
        <circle :cx="patternSize / 2" :cy="patternSize / 2" :r="patternSize / 20" :fill="strokeColor" :opacity="strokeOpacity" />
      </pattern>

      <!-- Diagonal Pattern -->
      <pattern v-if="pattern === 'diagonal'" :id="`mainPattern-${theme}`" :width="patternSize" :height="patternSize" patternUnits="userSpaceOnUse">
        <path :d="`M 0 ${patternSize} L ${patternSize} 0`" fill="none" :stroke="strokeColor" stroke-width="1" :opacity="strokeOpacity" />
        <path :d="`M -${patternSize / 2} ${patternSize / 2} L ${patternSize / 2} -${patternSize / 2}`" fill="none" :stroke="strokeColor" stroke-width="1" :opacity="strokeOpacity * 0.7" />
        <path :d="`M ${patternSize / 2} ${patternSize * 1.5} L ${patternSize * 1.5} ${patternSize / 2}`" fill="none" :stroke="strokeColor" stroke-width="1" :opacity="strokeOpacity * 0.7" />
      </pattern>

      <!-- Waves Pattern -->
      <pattern v-if="pattern === 'waves'" :id="`mainPattern-${theme}`" :width="patternSize" :height="patternSize" patternUnits="userSpaceOnUse">
        <path :d="`M 0 ${patternSize / 2} Q ${patternSize / 4} ${patternSize / 4} ${patternSize / 2} ${patternSize / 2} T ${patternSize} ${patternSize / 2}`" fill="none" :stroke="strokeColor" stroke-width="1" :opacity="strokeOpacity" />
        <path :d="`M 0 ${patternSize * 0.75} Q ${patternSize / 4} ${patternSize * 0.625} ${patternSize / 2} ${patternSize * 0.75} T ${patternSize} ${patternSize * 0.75}`" fill="none" :stroke="strokeColor" stroke-width="1" :opacity="strokeOpacity * 0.7" />
      </pattern>
    </defs>

    <rect width="900" height="1200" :fill="`url(#primaryGrad-${theme})`" />

    <circle cx="150" cy="200" r="250" :fill="`url(#accentGrad-${theme})`" :opacity="theme === 'light' ? 0.6 : 0.5" />
    <circle
      cx="750"
      cy="1000"
      r="300"
      :fill="`url(#accentGrad-${theme})`"
      :opacity="theme === 'light' ? 0.5 : 0.4"
    />

    <circle cx="200" cy="600" r="280" fill="#ffffff" :opacity="theme === 'light' ? 0.08 : 0.03" />
    <circle cx="700" cy="400" r="220" fill="#ffffff" :opacity="theme === 'light' ? 0.06 : 0.02" />

    <rect width="900" height="1200" :fill="`url(#mainPattern-${theme})`" />

    <!-- Shapes: Circles -->
    <g v-if="props.shapes === 'circles'" :opacity="theme === 'light' ? 0.25 : 0.18">
      <circle
        v-for="(shape, i) in shapesData.largeShapes"
        :key="`large-${i}`"
        :cx="shape.x"
        :cy="shape.y"
        :r="shape.size"
        :fill="i % 2 === 0 ? primaryColor : primaryColor700"
      />
    </g>

    <g v-if="props.shapes === 'circles'" :opacity="theme === 'light' ? 0.15 : 0.12">
      <circle
        v-for="(shape, i) in shapesData.smallShapes"
        :key="`small-${i}`"
        :cx="shape.x"
        :cy="shape.y"
        :r="shape.size"
        :fill="i % 2 === 0 ? primaryColor : primaryColor700"
      />
    </g>

    <!-- Shapes: Squares -->
    <g v-if="props.shapes === 'squares'" :opacity="theme === 'light' ? 0.25 : 0.18">
      <rect
        v-for="(shape, i) in shapesData.largeShapes"
        :key="`large-${i}`"
        :x="shape.x - shape.size / 2"
        :y="shape.y - shape.size / 2"
        :width="shape.size"
        :height="shape.size"
        rx="5"
        :fill="i % 2 === 0 ? primaryColor : primaryColor700"
      />
    </g>

    <g v-if="props.shapes === 'squares'" :opacity="theme === 'light' ? 0.15 : 0.12">
      <rect
        v-for="(shape, i) in shapesData.smallShapes"
        :key="`small-${i}`"
        :x="shape.x - shape.size / 2"
        :y="shape.y - shape.size / 2"
        :width="shape.size"
        :height="shape.size"
        rx="2"
        :fill="i % 2 === 0 ? primaryColor : primaryColor700"
      />
    </g>

    <!-- Shapes: Triangles -->
    <g v-if="props.shapes === 'triangles'" :opacity="theme === 'light' ? 0.25 : 0.18">
      <polygon
        v-for="(shape, i) in shapesData.largeShapes"
        :key="`large-${i}`"
        :points="`${shape.x},${shape.y - shape.size} ${shape.x + shape.size},${shape.y + shape.size} ${shape.x - shape.size},${shape.y + shape.size}`"
        :fill="i % 2 === 0 ? primaryColor : primaryColor700"
      />
    </g>

    <g v-if="props.shapes === 'triangles'" :opacity="theme === 'light' ? 0.15 : 0.12">
      <polygon
        v-for="(shape, i) in shapesData.smallShapes"
        :key="`small-${i}`"
        :points="`${shape.x},${shape.y - shape.size} ${shape.x + shape.size},${shape.y + shape.size} ${shape.x - shape.size},${shape.y + shape.size}`"
        :fill="i % 2 === 0 ? primaryColor : primaryColor700"
      />
    </g>

    <!-- Shapes: Hexagons -->
    <g v-if="props.shapes === 'hexagons'" :opacity="theme === 'light' ? 0.25 : 0.18">
      <polygon
        v-for="(shape, i) in shapesData.largeShapes"
        :key="`large-${i}`"
        :points="`
          ${shape.x},${shape.y - shape.size}
          ${shape.x + shape.size * 0.866},${shape.y - shape.size * 0.5}
          ${shape.x + shape.size * 0.866},${shape.y + shape.size * 0.5}
          ${shape.x},${shape.y + shape.size}
          ${shape.x - shape.size * 0.866},${shape.y + shape.size * 0.5}
          ${shape.x - shape.size * 0.866},${shape.y - shape.size * 0.5}
        `"
        :fill="i % 2 === 0 ? primaryColor : primaryColor700"
      />
    </g>

    <g v-if="props.shapes === 'hexagons'" :opacity="theme === 'light' ? 0.15 : 0.12">
      <polygon
        v-for="(shape, i) in shapesData.smallShapes"
        :key="`small-${i}`"
        :points="`
          ${shape.x},${shape.y - shape.size}
          ${shape.x + shape.size * 0.866},${shape.y - shape.size * 0.5}
          ${shape.x + shape.size * 0.866},${shape.y + shape.size * 0.5}
          ${shape.x},${shape.y + shape.size}
          ${shape.x - shape.size * 0.866},${shape.y + shape.size * 0.5}
          ${shape.x - shape.size * 0.866},${shape.y - shape.size * 0.5}
        `"
        :fill="i % 2 === 0 ? primaryColor : primaryColor700"
      />
    </g>

    <!-- Shapes: Mixed -->
    <g v-if="props.shapes === 'mixed'" :opacity="theme === 'light' ? 0.25 : 0.18">
      <template v-for="(shape, i) in shapesData.largeShapes" :key="`large-${i}`">
        <circle v-if="i % 4 === 0" :cx="shape.x" :cy="shape.y" :r="shape.size" :fill="primaryColor" />
        <rect
          v-else-if="i % 4 === 1"
          :x="shape.x - shape.size / 2"
          :y="shape.y - shape.size / 2"
          :width="shape.size"
          :height="shape.size"
          rx="5"
          :fill="primaryColor700"
        />
        <polygon
          v-else-if="i % 4 === 2"
          :points="`${shape.x},${shape.y - shape.size} ${shape.x + shape.size},${shape.y + shape.size} ${shape.x - shape.size},${shape.y + shape.size}`"
          :fill="primaryColor"
        />
        <polygon
          v-else
          :points="`
            ${shape.x},${shape.y - shape.size}
            ${shape.x + shape.size * 0.866},${shape.y - shape.size * 0.5}
            ${shape.x + shape.size * 0.866},${shape.y + shape.size * 0.5}
            ${shape.x},${shape.y + shape.size}
            ${shape.x - shape.size * 0.866},${shape.y + shape.size * 0.5}
            ${shape.x - shape.size * 0.866},${shape.y - shape.size * 0.5}
          `"
          :fill="primaryColor700"
        />
      </template>
    </g>

    <g v-if="props.shapes === 'mixed'" :opacity="theme === 'light' ? 0.15 : 0.12">
      <template v-for="(shape, i) in shapesData.smallShapes" :key="`small-${i}`">
        <circle v-if="i % 3 === 0" :cx="shape.x" :cy="shape.y" :r="shape.size" :fill="primaryColor" />
        <rect
          v-else-if="i % 3 === 1"
          :x="shape.x - shape.size / 2"
          :y="shape.y - shape.size / 2"
          :width="shape.size"
          :height="shape.size"
          rx="2"
          :fill="primaryColor700"
        />
        <polygon
          v-else
          :points="`${shape.x},${shape.y - shape.size} ${shape.x + shape.size},${shape.y + shape.size} ${shape.x - shape.size},${shape.y + shape.size}`"
          :fill="primaryColor"
        />
      </template>
    </g>
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface AuthBackgroundProps {
  theme?: 'light' | 'dark'
  pattern?: 'grid' | 'hexagon' | 'triangle' | 'diamond' | 'dots' | 'diagonal' | 'waves'
  shapes?: 'circles' | 'squares' | 'triangles' | 'hexagons' | 'mixed'
  patternSize?: number
  randomPositions?: boolean
}

const props = withDefaults(defineProps<AuthBackgroundProps>(), {
  theme: 'light',
  pattern: 'grid',
  shapes: 'circles',
  patternSize: 100,
  randomPositions: false
})

const primaryColor = computed(() => {
  if (typeof window === 'undefined') return '#6ba101'
  return getComputedStyle(document.documentElement).getPropertyValue('--p-primary-color').trim() || '#6ba101'
})

const primaryColor700 = computed(() => {
  if (typeof window === 'undefined') return '#4d7501'
  return getComputedStyle(document.documentElement).getPropertyValue('--p-primary-700').trim() || '#4d7501'
})

const strokeColor = computed(() => (props.theme === 'light' ? primaryColor700.value : 'white'))
const strokeOpacity = computed(() => (props.theme === 'light' ? 0.2 : 0.1))

// Generador de números pseudoaleatorios determinista
const seededRandom = (seed: number) => {
  let value = seed
  return () => {
    value = (value * 9301 + 49297) % 233280
    return value / 233280
  }
}

// Generar posiciones aleatorias
const generateRandomShapes = () => {
  const seed = props.randomPositions ? Date.now() : (props.theme === 'light' ? 12345 : 54321)
  const random = seededRandom(seed)

  const largeShapes = Array.from({ length: 8 }, () => ({
    x: random() * 850 + 25,
    y: random() * 1150 + 25,
    size: random() * 40 + 20
  }))

  const smallShapes = Array.from({ length: 6 }, () => ({
    x: random() * 880 + 10,
    y: random() * 1180 + 10,
    size: random() * 8 + 5
  }))

  return { largeShapes, smallShapes }
}

// Posiciones fijas predefinidas
const fixedShapes = {
  largeShapes: [
    { x: 450, y: 150, size: 40 },
    { x: 800, y: 550, size: 50 },
    { x: 150, y: 850, size: 35 },
    { x: 550, y: 950, size: 60 },
    { x: 300, y: 1100, size: 45 },
    { x: 650, y: 250, size: 25 },
    { x: 350, y: 450, size: 30 },
    { x: 100, y: 350, size: 20 }
  ],
  smallShapes: [
    { x: 850, y: 150, size: 8 },
    { x: 400, y: 750, size: 10 },
    { x: 200, y: 1050, size: 7 },
    { x: 750, y: 700, size: 9 },
    { x: 500, y: 550, size: 12 },
    { x: 100, y: 1150, size: 10 }
  ]
}

const shapesData = computed(() => props.randomPositions ? generateRandomShapes() : fixedShapes)
</script>
